/// <reference path="../../app.definition.ts" />

/*
* eCareVault.INudgeService
*
* author: Ian Tripp
*/
import angular from "angular";
import { nudgeTours } from "@common/nudges/tours";
import { IUserStateService } from "@common/service/userState.service";
import { IAppStateService } from "@common/service/appState.service";
import { CacheService } from "@common/service/cache.service";
import * as SVG from "@common/utility/svg";
import * as HTMLUtil from "@common/utility/html";
export interface INudgeService {
    nudgeKeywords: any;
    activeTour: NudgeTour
    tourIsActive(name?: string): boolean;
    preNavigate(view: string): void;
    postNavigate(view: string): void;
    checkForTourTrigger(stateName: string): void;
    clearOverlay(): void;
    refreshOverlay(): void;
    init(): void;
    endTour(disable?: boolean);
    triggerTour(name: string);
    getToursForView(viewName: string): NudgeTour[];
}


// This object maps to the elements that will form holes in the overlay
export class NudgeElement {
    selector: string; // #elementId
    el: any; // jQuery reference to element
    shape: string; //Square, rectangle, circle
    layer: string; //Which frame layer this is over
    padding: number; //Distance of the frame from the element
    onClick: string; //What to do when user clicks on element
    shift: { up?: number, down?: number, left?: number, right?: number }; //How much to shift the hole around the element

    // Set with loadElementInfo()
    offset: HTMLUtil.Offset; //location of element on page
    size: HTMLUtil.ElementSize; //size of the element in question

    constructor(obj?: any) {
        !!obj && this.deserialize(obj);
    }

    // Calculate the frame offset and size, and create the SVG shape for the hole
    createFrame = (): SVG.Shape => {
        let frameSize: HTMLUtil.ElementSize = {
            innerWidth: this.size.innerWidth + 2 * this.padding,
            innerHeight: this.size.innerHeight + 2 * this.padding,
            outerHeight: 0,
            outerWidth: 0
        }

        let frameOffset: HTMLUtil.Offset = {
            left: this.offset.left - this.padding,
            top: this.offset.top - this.padding,
            shiftLeft: undefined,
            shiftTop: undefined
        }

        return new SVG.Shape(this.shape, frameSize, frameOffset, SVG.COUNTER_CLOCKWISE);
    }

    // Get the information about the html element(s) and set the size and offset
    loadElementInfo = (anchor: string): void => {
        let elOffset: any = null;
        let elSize: any = null;
        // console.log("Getting element info for:", this.selector);
        // If more than one element selector (comma separated)
        if (this.selector.indexOf(',') > -1) {
            this.el = null;
            let selectorStrings: string[] = this.selector.split(',');
            // console.log(selectorStrings);
            let selectors: string[] = [];
            for (let i = 0; i < selectorStrings.length; i++) {
                selectors.push(selectorStrings[i].trim());
                // console.log("Adding Selector:", selectorStrings[i].trim());
            }

            //Calculate the box surrounding all elements
            // console.log("Debug error", selectors,anchor);
            let bb = HTMLUtil.getBoundingBox(selectors, anchor);
            this.offset = bb.offset; // set offset and size to bounding box properties
            this.size = bb.size;
            // console.log("Element bb loaded:", this);
        } else { // Only one element in selector
            // Get the size and offset of the element in relation to the anchor of the element
            this.el = angular.element(this.selector);
            elOffset = anchor === 'body' ? this.el.offset() : this.el.position();
            this.offset = {
                left: elOffset.left,
                top: elOffset.top,
                shiftLeft: undefined,
                shiftTop: undefined
            };

            this.size = {
                innerWidth: this.el.width(),
                innerHeight: this.el.height(),
                outerWidth: this.el.outerWidth(),
                outerHeight: this.el.outerHeight()
            }
        }

        // Shift the offset based on the shift property
        let horzShift = !!this.shift ? (this.shift.right || 0) - (this.shift.left || 0) : 0;
        let vertShift = !!this.shift ? (this.shift.down || 0) - (this.shift.up || 0) : 0;

        this.offset.left += horzShift;
        this.offset.top += vertShift;
    }


    deserialize = (obj: any): NudgeElement => {
        !!obj.selector && (this.selector = obj.selector);
        !!obj.shape && (this.shape = obj.shape);
        this.padding = !!obj.padding ? obj.padding : 0;
        !!obj.onClick && (this.onClick = obj.onClick);
        !!obj.offset && (this.offset = obj.offset);
        !!obj.size && (this.size = obj.size);
        !!obj.shift && (this.shift = obj.shift);
        this.layer = obj.layer || 'anchor';
        return this;
    }
}

// This class represents the messages that are placed about the screen over the tour frame
export class NudgeMessage {
    message: string; // Text of message (supports HTML tags but is not angular compiled)
    anchor: string; // The element this message anchored to
    layer: string; // The frame layer the message gets attached to (should it scroll with the body or stay fixed with the frame)
    location: string; // Which side of the anchor the message is on
    distance: number; // Distance from the element the message should sit
    font: string; // Font of the message text
    color: string; // Color of the message text
    maxWidth: number; // Maximum width of the message element
    endTour: string; // (Optional) side of the message the 'End This Tour' should be displayed on
    type: string
    shift: { up?: number, down?: number, left?: number, right?: number }; //Shift the message around in any direction

    constructor(obj?: any) {
        !!obj && this.deserialize(obj);
    }

    deserialize = (obj: any): NudgeMessage => {
        !!obj.message && (this.message = obj.message);
        !!obj.anchor && (this.anchor = obj.anchor);
        this.maxWidth = obj.maxWidth || 10000;
        this.layer = obj.layer || 'anchor';
        !!obj.location && (this.location = obj.location);
        !!obj.distance && (this.distance = obj.distance);
        !!obj.endTour && (this.endTour = obj.endTour);
        !!obj.shift && (this.shift = obj.shift);
        this.type = obj.type || 'how-message';
        this.font = obj.font || 'Avenir Helvetica';
        this.color = obj.color || '#000';
        return this;
    }
}

// This class handles the logic around when a tour should start
class NudgeTrigger {
    views: string[]; //The views this trigger can be activated from
    step: number; // Step of the tour this trigger starts the tour at
    userStates: { state: string, val: any }[] //the user states that trigger this tour
    appStates: { state: string, val: any }[] //the application states that trigger this tour

    constructor(obj?: any) {
        !!obj && this.deserialize(obj);
    }

    deserialize = (obj: any): NudgeTrigger => {
        !obj.views && !!obj.view && (this.views = [obj.view]);
        !obj.view && !!obj.views && (this.views = obj.views);
        !obj.userStates && !!obj.userState && (this.userStates = [obj.userState]);
        !obj.userState && !!obj.userStates && (this.userStates = obj.userStates);
        !obj.appStates && !!obj.appState && (this.appStates = [obj.appState]);
        !obj.appState && !!obj.appStates && (this.appStates = obj.appStates);
        this.step = !!obj.step ? this.step : 0;
        return this;
    }

    // Is this tour triggered on this view?
    checkView = (viewName: string): boolean => (!!this.views.find((view) => view == viewName));

    // Check if the trigger's user-states are activated
    checkUserState = ($_userState: IUserStateService, $_appState: IAppStateService): boolean => {
        for (let uState of this.userStates || []) {
            if (!$_userState.checkState(uState.state, uState.val)) {
                return false; // If any state doesn't match, fail trigger
            }
        }

        // console.log("$_appState:", $_appState);
        // Check the application state functions
        for (let aState of this.appStates || []) {
            let stateFn = $_appState.getState(aState.state);
            // console.log("Function:", stateFn, ", Result:", stateFn(aState.val));
            if (!stateFn(aState.val)) {
                return false; // If any state doesn't match, fail trigger
            }
        }

        return true; // This trigger passed all states and the tour should start
    }
}

// Represents the overall tour
export class NudgeTour {
    name: string; //Name of the tour
    steps: NudgeStep[]; // Each step of the tour
    priority: number;
    helpViews: any[];
    displayName: string;
    triggers: NudgeTrigger[]; // The states that trigger the tour
    cancelMessage: string; // Custom wording to replace 'End this Tour'
    userState: string; //User state for whether the tour is completed or not


    constructor(obj?: any) {
        !!obj && this.deserialize(obj);
    }

    deserialize = (obj: any): NudgeTour => {
        if (!obj.name) {
            // console.error("Cannot create NudgeTour from definition, missing name:", obj);
        }
        this.name = obj.name;
        this.priority = obj.priority || 100;
        !!obj.steps && (this.steps = obj.steps);
        !!obj.userState && (this.userState = obj.userState);
        !!obj.triggers && (this.triggers = obj.triggers);
        !!obj.helpViews && (this.helpViews = obj.helpViews);
        this.displayName = obj.displayName || obj.name;
        this.cancelMessage = !!obj.cancelMessage ? obj.cancelMessage : "End this Tour";
        return this;
    }

}

// Represents the states from which a tour can continue
export class NudgeContinue {
    action: string; //Type of continue action (navigation, element click)
    val: any; // Value matching the action (UI State name in case of navigation)
    step: number; //Which step of the tour it should resume on

    constructor(obj?: any) {
        !!obj && this.deserialize(obj);
    }

    deserialize = (obj: any): NudgeContinue => {
        !!obj.action && (this.action = obj.action);
        !!obj.val && (this.val = obj.val);
        !!obj.step && (this.step = obj.step);
        return this;
    }

}

// Represents a single step of the tour
export class NudgeStep {
    step: number;
    scrollTo: boolean;
    nextStep: number;
    anchor: string; // An element that contains all of the NudgeElements (Defaults to 'body')
    frame: { // Elements that form the frame around the page (header, side-nav, footer in eCVs case), all optional
        top?: string,
        right?: string,
        bottom?: string,
        left?: string
    }

    continues: NudgeContinue[]; // The different ways this tour can be continued
    elements: NudgeElement[]; // The elements that form the holes in the overlay
    events: any[];
    messages: NudgeMessage[]; // The messsages to be shown around the screen
    watchers: any; //functions that will watch different variables and states

    onSuccess: { //What happens when this tour ends successfully (currently always hapens when tour ends)
        action: string,
        key: string,
        val: any
    };

    constructor(obj?: any) {
        !!obj && this.deserialize(obj);
    }

    deserialize = (obj: any): NudgeStep => {
        this.step = obj.step || 0;
        this.anchor = obj.anchor || 'body';
        this.nextStep = obj.nextStep || this.step + 1;
        this.scrollTo = !!obj.scrollTo;
        !!obj.elements && (this.elements = obj.elements);
        !!obj.events && (this.events = obj.events);
        !!obj.messages && (this.messages = obj.messages);
        !!obj.continues && (this.continues = obj.continues);
        !!obj.onSuccess && (this.onSuccess = obj.onSuccess);
        !!obj.watchers && (this.watchers = obj.watchers);
        !!obj.frame && (this.frame = obj.frame);
        return this;
    }
}

//Represents the entire overlay that is shown over the screen
export class NudgeOverlay {
    id: string; //HTML element id assigned to the overlay
    anchor: string; //Local copy of the step anchor

    $_nudge: INudgeService; //Reference to the NudgeService
    $overlayFrame: JQuery<HTMLElement>; //HTML element for the Overlay Frame (fixed elements around the edge of the screen)
    $overlayAnchor: JQuery<HTMLElement>; //HTML element for the Overlay anchor (element that contains each of the step elements)
    $overlayAnchorGap: JQuery<HTMLElement>; //HTML element for the Overlay anchor gap (element that spans the gap
    //// between inside of frame and outside of anchor)

    $overlay: ng.IAugmentedJQuery; //(Obsolete) HTML element for the overall overlay
    $messages: ng.IAugmentedJQuery; //(Obsolete) HTML element for the messages layer
    $frames: ng.IAugmentedJQuery; //(Obsolete) HTML element for the holes in the overlay
    $svg: ng.IAugmentedJQuery; //(Obsolete) HTML element for the SVG object

    resizeTimeout: any; //Timer reference to slow down resize calls
    step: NudgeStep; // Step of the tour this overlay goes over

    constructor($_nudge: INudgeService, tour: NudgeTour, stepNumber: number, anchor?: string) {
        this.step = tour.steps[stepNumber];
        this.$_nudge = $_nudge;
        this.anchor = this.step.anchor;
        this.id = 'nudge-overlay-' + tour.name;
    }


    // Create a layer for the overlay
    buildLayer = (layer, framingEl, cutout?, fill?, opacity?, scrollTop?): string => {
        let $framing = framingEl === 'window' ? angular.element(window) : angular.element(framingEl); // Get the element framing the layer
        let framingSize: HTMLUtil.ElementSize = {
            innerWidth: $framing.innerWidth(),
            innerHeight: $framing.innerHeight(),
            outerWidth: 0,
            outerHeight: 0
        }; // Size of the framing element
        let shapes: SVG.Shape[] = [  //Create a list of shapes to create the SVG objet
            new SVG.Shape('rect', framingSize, SVG.ORIGIN, SVG.CLOCKWISE) //Set the frame as the first shape (CLOCKWISE indicates filled shape)
        ];

        //If there is a cutout in the frame, add it to the shapes list (COUNTER_CLOCKWISE indicates empty shape)
        !!cutout && shapes.push(new SVG.Shape('rect', cutout.size, cutout.offset, SVG.COUNTER_CLOCKWISE));

        // Add each of the elements to the layer
        for (let el of this.step.elements || []) {
            if (el.layer === layer) {
                // console.log("Adding element to layer", el, layer);
                el.loadElementInfo(framingEl); // locate element on page and get size and offset of element
                !!scrollTop && el.offset.top < scrollTop.y && (scrollTop.y = el.offset.top);
                shapes.push(el.createFrame()); //Add the element cutout to the list of shapes
            }
        }

        let elSize: HTMLUtil.ElementSize = {
            innerWidth: $framing.width(),
            innerHeight: $framing.height(),
            outerWidth: 0,
            outerHeight: 0
        }; // Size of the framing element
        // Use the SVG module functions to create an SVG path from all of the shapes
        let svgText: string = SVG.createSVGFromPaths(
            elSize,
            [new SVG.Path(shapes, fill, opacity)],
            { 'pointer-events': 'visibleFill' }
        );

        // console.log("SVG Layer[" + layer + "]:", svgText);

        return svgText; //return SVG element created for the layer
    }

    // Create the entire overlay
    create = ($timeout, $sce, $rootScope, $compile): void => {
        let $win = angular.element(window); // reference to window element
        let $body = angular.element('body'); // reference to body element
        $(document).scrollTop(0); // scroll the body to the top
        let windowSize = { w: $win.innerWidth(), h: $win.innerHeight() }; //size of the window in px
        let bodySize = { w: $body.width(), h: $(document).innerHeight() }; //size of the body in px
        let shapes: SVG.Shape[] = null; //List that is reused for creating SVG shapes
        let path: SVG.Path = null; // SVG Path object that is reused for creating SVG elements
        this.$overlayFrame = null; // Reset the overlay layer elements
        this.$overlayAnchor = null;
        this.$overlayAnchorGap = null;
        let cutoutSize = null; // Reused variables for size/offset of overlay cutouts
        let cutoutOffset = null;
        let scrollTop = { y: 100000 };

        // this.$overlay = angular.element(`<div class="nudge-overlay"></div>`);
        // this.$messages = angular.element(`<div class="nudge-messages-container"></div>`);
        // this.$frames = angular.element(`<div class="nudge-frame-container"></div>`);


        // console.log("Creating overlay for tour", this.$_nudge.activeTour, "on step", this.step);

        // If this step has a frame around it, start there
        if (!!this.step.frame) {
            // console.log(" - Found a view frame, creating...");
            // get the elements surrounding the page
            let $top = this.step.frame.top ? angular.element(this.step.frame.top) : null,
                $right = this.step.frame.right ? angular.element(this.step.frame.right) : null,
                $bottom = this.step.frame.bottom ? angular.element(this.step.frame.bottom) : null,
                $left = this.step.frame.left ? angular.element(this.step.frame.left) : null;

            // Measure the frame cutout size & offset (space in the middle of the page not covered by the frame)
            cutoutSize = {
                w: windowSize.w - ($right ? windowSize.w - $right.offset().left : 0) - ($left ? $left.outerWidth() + $left.offset().left : 0),
                h: windowSize.h - ($top ? $top.outerHeight() + $top.offset().top : 0) - ($bottom ? windowSize.h - $bottom.offset().top : 0)
            }

            cutoutOffset = {
                x: ($left ? $left.outerWidth() + $left.offset().left : 0),
                y: ($top ? $top.outerHeight() + $top.offset().top : 0)
            }

            // console.log("Cutout from frame:", cutoutSize, cutoutOffset);
            //Build the layer and overlay frame HTLM elements and append them to the overlay frame element
            let frameLayer = this.buildLayer('frame', 'window', { size: cutoutSize, offset: cutoutOffset }, 'black', '0.75', scrollTop);
            this.$overlayFrame = angular.element(`<div class="nudge-overlay-outer-frame">` + frameLayer + `</div>`);
            this.$overlayFrame.css({ "display": "none" });
            $body.append(this.$overlayFrame);
        }

        // Build Anchor SVG

        // anchorSplit represents the fact that the element surrounds the step
        // elements may not scroll with the body (internal scrolling)
        // Currently obsolete and not working well
        let anchorSplit = this.anchor.split(' '); //elements separated by space
        let $outerBound = null;
        let $innerBound = null;

        if (anchorSplit.length > 1) { //If there is an anchor split
            $outerBound = angular.element(anchorSplit[0]);
            $innerBound = angular.element(anchorSplit[1]);
        } else { //Only one anchor selector provided
            $innerBound = $outerBound = angular.element(this.anchor);
        }

        // Calculate the  size and offset of the bounding elements
        let innerSize = { w: $innerBound.width(), h: $innerBound.height() };
        let outerSize: HTMLUtil.ElementSize = {
            outerWidth: $outerBound.width(),
            outerHeight: $innerBound.height(),
            innerWidth: 0,
            innerHeight: 0
        };
        let outerOffset: HTMLUtil.Offset = {
            left: $outerBound.offset().left - cutoutOffset.x,
            top: $outerBound.offset().top - cutoutOffset.y,
            shiftLeft: undefined,
            shiftTop: undefined
        };

        // Build the anchor layer and overlay anchor elements, and append them to the outer bounded object
        let anchorLayer = this.buildLayer('anchor', anchorSplit[0], null, 'black', '0.75', scrollTop);
        this.$overlayAnchor = angular.element(`<div class="nudge-overlay">` + anchorLayer + `</div>`);
        this.$overlayAnchor.css({ "display": "none" });
        $outerBound.append(this.$overlayAnchor);

        // Build Anchor Gap
        if (!!this.step.frame) {
            // Get the size of the inner cutout (empty space that will be filled by the anchor)
            let innerCutoutSize: HTMLUtil.ElementSize = {
                innerWidth: cutoutSize.w,
                innerHeight: bodySize.h - cutoutOffset.y - (windowSize.h - cutoutSize.h - cutoutOffset.y),
                outerWidth: 0,
                outerHeight: 0
            };

            // console.log("Outer anchor-gap:", innerCutoutSize, cutoutOffset);

            // List of the shapes for the anchor gap
            shapes = [
                // Initialize with filled in and cut out areas
                new SVG.Shape('rect', innerCutoutSize, SVG.ORIGIN, SVG.CLOCKWISE),
                new SVG.Shape('rect', outerSize, outerOffset, SVG.COUNTER_CLOCKWISE)
            ];

            // Create the SVG element for the anchorGap
            let anchorGapLayer = SVG.createSVGFromPaths(
                innerCutoutSize,
                [new SVG.Path(shapes, 'black', '0.75')],
                { 'pointer-events': 'visibleFill' }
            );

            // console.log("SVG Layer[anchorGap]:", anchorGapLayer);
            // Append the SVG to the anchorGap element, append the anchorGap element to the outerBound element
            this.$overlayAnchorGap = angular.element(`<div class="nudge-overlay anchor-gap">` + anchorGapLayer + `</div>`);
            this.$overlayAnchorGap.css("display", "none");
            $outerBound.append(this.$overlayAnchorGap);

            // Nudge the anchorGap into place with some CSS magic
            this.$overlayAnchorGap.css({
                "top": (-outerOffset.top) + 'px',
                "left": (-outerOffset.left) + 'px',
                "width": cutoutSize.w + 'px',
                "overflow": "hidden"
            });

            //
            // $body.bind('resize', () => {
            //     $timeout.cancel(this.resizeTimeout);
            //     this.resizeTimeout = $timeout(() => {
            //         this.destroy();
            //         this.create($timeout, $sce, $rootScope, $compile);
            //     });
            // }, 250);
        }


        this.$overlayFrame.fadeIn(250);
        this.$overlayAnchor.fadeIn(250);
        this.$overlayAnchorGap.fadeIn(250);

        // Create all of the message elements

        let endNearMsg: boolean = false; // Gets set to true if one of the messages has the end tour CTA attached to it
        let $endTour = null; // end tour element

        // Create each message and set proper location
        for (let msg of this.step.messages) {
            // console.log("creating message:", msg);
            let message = msg.message;

            // Replace application specific keywords with their current variable value
            for (let keyword in this.$_nudge.nudgeKeywords) {
                message = message.split(keyword).join(this.$_nudge.nudgeKeywords[keyword]() || 'undefined');
            }

            // Create the message element and append the message text (HTML is marked as safe)
            let $msgEl = angular.element(`<div class="nudge-message">` + $sce.trustAs('html', message) + `</div>`);
            let $msgAnchor = angular.element(msg.anchor); // Get a reference to the element message is anchored to

            // Get the layer that the message gets appended to
            let $layerEl = null;

            switch (msg.layer) {
                case 'frame': $layerEl = this.$overlayFrame; break;
                case 'anchor-gap': $layerEl = this.$overlayFrame; break;
                case 'anchor':
                default: $layerEl = this.$overlayAnchor;
            }

            // Get size and offset of anchor
            let anchorSize = {
                w: $msgAnchor.outerWidth(),
                h: $msgAnchor.outerHeight()
            };

            let anchorOffset = this.step.anchor === 'body' ? {
                x: $msgAnchor.offset().left,
                y: $msgAnchor.offset().top
            } : {
                x: $msgAnchor.position().left,
                y: $msgAnchor.position().top
            };

            // append the message element to proper layer
            $layerEl.append($msgEl);

            // set width CSS properties
            $msgEl.css({
                'width': "auto",
                'max-width': (msg.maxWidth || 10000) + "px"
            });

            // get the actual size of the element
            let msgElSize = {
                w: $msgEl.outerWidth(),
                h: $msgEl.outerHeight()
            };

            let top = null, left = null;

            // Based on location of message in relation to anchor, find offset
            switch (msg.location) {
                case 'right':
                    top = (anchorSize.h - msgElSize.h) / 2 + anchorOffset.y;
                    left = anchorOffset.x + anchorSize.w + msg.distance;
                    break;
                case 'left':
                    top = (anchorSize.h - msgElSize.h) / 2 + anchorOffset.y;
                    left = anchorOffset.x - msg.distance - msgElSize.w;
                    break;
                case 'bottom':
                    top = anchorOffset.y + anchorSize.h + msg.distance;// + msgElSize.h;
                    left = (anchorSize.w - msgElSize.w) / 2 + anchorOffset.x;
                    break;
                case 'top':
                default:
                    top = anchorOffset.y - msg.distance - msgElSize.h;
                    left = (anchorSize.w - msgElSize.w) / 2 + anchorOffset.x;
                    break;
            }

            // Create an element for the arrow/triangle that is attached to the message and points to its anchor
            let $arrow = angular.element(`<div class="message-arrow message-arrow-$DIR"></div>`.replace(/\$DIR/g, msg.location));

            // Shift the message location based on the optional shift property
            let msgHorzShift = !!msg.shift ? (msg.shift.right || 0) - (msg.shift.left || 0) : 0;
            let msgVertShift = !!msg.shift ? (msg.shift.down || 0) - (msg.shift.up || 0) : 0;


            (top + msgVertShift) < scrollTop.y && (scrollTop.y = top + msgVertShift);

            // Place the message in the proper location
            $msgEl.css({
                'top': top + msgVertShift + 'px',
                'left': left + msgHorzShift + 'px',
                // 'color': msg.color,
                'font': msg.font
            }).addClass(msg.type || 'how-message').append($arrow); //and append the arrow

            // If end tour CTA hasn't already been place near a message and this message has it attached
            if (!endNearMsg && !!msg.endTour) {
                endNearMsg = true;
                // Create the end tour CTA element
                $endTour = angular.element(`
                        <div class="nudge-end-tour-cont on-message" >
                            <span>
                                <button class="end-tour">
                                    End this Tour
                                </button>
                            </span>
                        </div>
                    `);


                msgElSize = {
                    w: $msgEl.outerWidth(),
                    h: $msgEl.outerHeight()
                };

                // Set proper click action for ending the tour
                $endTour.find('.end-tour').on('click', () => this.$_nudge.endTour());
                $msgEl.append($endTour); //append it to the message
                let cssObj: any;

                // Properly place the end tour CTA next to the message
                let endTourSize = {
                    w: $endTour.outerWidth(),
                    h: Math.floor($endTour.outerHeight())
                }

                // Adjust endTour height in case it is has an odd number of pixels in height
                endTourSize.h = Math.ceil(endTourSize.h);
                if (endTourSize.h % 2 != 0) {
                    endTourSize.h -= 1;
                }

                switch (msg.endTour) {
                    case 'top':
                        cssObj = {
                            'top': (-endTourSize.h - 10) + 'px',
                            'left': ((msgElSize.w - endTourSize.w) / 2) + 'px'
                        }
                        break;
                    case 'left':
                        cssObj = {
                            'top': ((msgElSize.h - endTourSize.h) / 2) + 'px',
                            'left': (-endTourSize.w - 10) + 'px'
                        }
                        break;
                    case 'right':
                        cssObj = {
                            'top': ((msgElSize.h - endTourSize.h) / 2) + 'px',
                            'left': (msgElSize.w + 10) + 'px'
                        }
                        break;
                    case 'bottom':
                    default:
                        cssObj = {
                            'top': (msgElSize.h + 10) + 'px',
                            'left': ((msgElSize.w - endTourSize.w) / 2) + 'px'
                        }
                }
                cssObj.top < scrollTop.y && (scrollTop.y = cssObj.top);
                cssObj.width = '110px';
                cssObj.height = endTourSize.h + 'px';
                cssObj['text-align'] = 'center';
                $endTour.css(cssObj);
            }
        }

        // If end tour CTA not placed near a message, place it in the top left corner of the page
        if (!$endTour) {
            $endTour = angular.element(`
                    <div class="nudge-end-tour-cont default">
                        <span> Already got this?
                            <button class="end-tour btn orange-button">End Tour</button>
                        </span>
                    </div>
                `);

            this.$overlayFrame.append($endTour);
        }

        $endTour.find('.end-tour').on('click', () => this.$_nudge.endTour());

        !!this.step.scrollTo && $(document).scrollTop(scrollTop.y); // scroll the body to the highest element or messages
        // this.$overlay.append(this.$frames).append(this.$messages);
    }

    // Remove all of the elements from the page and set their references to null
    destroy = (): boolean => {
        !!this.$overlayFrame && this.$overlayFrame.remove() && (this.$overlayFrame = null);

        !!this.$overlayAnchor && this.$overlayAnchor.remove() && (this.$overlayAnchor = null);

        !!this.$overlayAnchorGap && this.$overlayAnchorGap.remove() && (this.$overlayAnchorGap = null);
        return true;
        // !!this.$svg && this.$svg.remove() && (this.$svg = null);
        // !!this.$frames && this.$frames.remove() && (this.$frames = null);
        // !!this.$messages && this.$messages.remove() && (this.$messages = null);
        // !!this.$overlay && this.$overlay.remove() && (this.$overlay = null);
    };
}

// Angular service that handles manages Nudge logic
export class NudgeService implements INudgeService {
    tours: NudgeTour[]; //List of all tours available
    activeTour: NudgeTour; //Currently active tour, if any
    currentStep: NudgeStep; //Current step of active tour
    currentStepNumber: number; //Number of the current step
    overlay: NudgeOverlay; //Reference to the overlay object created for each step
    sizeWatchers: Function[];

    static $inject = ['UserStateService', 'CacheService', 'AppStateService', '$timeout', '$sce', '$rootScope', '$compile'];
    constructor(
        private $_userState: IUserStateService,
        private $_cache: CacheService,
        private $_appState: IAppStateService,
        private $timeout: ng.ITimeoutService,
        private $sce,
        private $rootScope,
        private $compile
    ) { }

    // Application specific keywords that will get replaced with their current value when messages are parsed
    nudgeKeywords: any = {
        '$TEAM_SPEC': () => {
            let sub = this.$_cache.currentSubject;
            console.log("$TEAM_SPEC subject:", sub);
            return !!sub ? !!sub.projectName ? "members on the " + sub.projectName + " Team" : "people who care for " + sub.firstName : null;
        },
        '$MANAGE_SPEC': () => {
            let sub = this.$_cache.currentSubject;
            console.log("$TEAM_SPEC subject:", sub);
            return !!sub ? !!sub.projectName ? "work on the " + sub.projectName + " Project" : "who help care for " + sub.firstName : null;
        },
        '$CARE_SPEC': () => {
            let sub = this.$_cache.currentSubject;
            console.log("$TEAM_SPEC subject:", sub);
            return !!sub ? !!sub.projectName ? sub.projectName : sub.firstName + "'s care!" : null;
        },
        '$DOC_SPEC': () => {
            let sub = this.$_cache.currentSubject;
            console.log("$TEAM_SPEC subject:", sub);
            return !!sub ? !!sub.projectName ? "documents" : sub.firstName + "'s documents!" : null;
        },
        '$SUBJECT_FIRST': () => {
            let sub = this.$_cache.currentSubject;
            return !!sub ? !!sub.projectName ? sub.projectName : sub.firstName : null;
        },
        '$DOC_START': () => {
            let sub = this.$_cache.currentSubject;
            return !!sub ? !!sub.projectName ? "Have documents you need to store or share securely with the Team?" : "Tired of trying to keep up with piles of documents?" : null;
        }
    };

    // Initialize the NudgeService
    init = (): void => {
        // On press of escape, if there is an active tour, end the tour
        angular.element(document).on('keyup',
            ($event) => {
                $event.keyCode == 27 && this.tourIsActive() && this.endTour();
            }
        )

        //Load all of the tours from the Tours module using a helper from the Nudge module
        this.tours = [];

        // For each tour, get it's definition and instantiate Nudge objects
        for (let tourDef of nudgeTours() || []) {
            let steps: NudgeStep[] = [];
            let triggers: NudgeTrigger[] = [];

            // Create each step
            for (let tourStep of tourDef.steps || []) {
                let elements: NudgeElement[] = [];
                let messages: NudgeMessage[] = [];
                let continues: NudgeContinue[] = [];

                // Deserialize the NudgeElements
                for (let stepElement of tourStep.elements || []) {
                    let ne = new NudgeElement(stepElement);
                    elements.push(ne);
                }

                // Deserialize the NudgeMessages
                for (let stepMessage of tourStep.messages || []) {
                    let nm = new NudgeMessage(stepMessage);
                    messages.push(nm);
                }

                // Deserialize the NudgeContinues
                for (let stepContinue of tourStep.continues || []) {
                    let nc = new NudgeContinue(stepContinue);
                    continues.push(nc);
                }

                // Create the NudgeStep object with augmented step definition
                steps.push(new NudgeStep({
                    step: tourStep.step,
                    events: tourStep.events,
                    frame: tourStep.frame,
                    nextStep: tourStep.nextStep,
                    anchor: tourStep.anchor,
                    elements: elements,
                    messages: messages,
                    watchers: tourStep.watchers,
                    continues: continues,
                    onSuccess: tourStep.onSuccess
                }));
            }

            // Deserialize the NudgeTriggers
            for (let tourTrigger of tourDef.triggers || []) {
                triggers.push(new NudgeTrigger(tourTrigger));
            }

            // Append the tour to the list
            this.tours.push(new NudgeTour({
                name: tourDef.name,
                displayName: tourDef.displayName,
                userState: tourDef.userState,
                helpViews: tourDef.helpViews,
                steps: steps,
                triggers: triggers,
                priority: tourDef.priority
            }));
        }
    }

    // Destroy the current overlay, if any
    clearOverlay = (): boolean => {
        return !!this.overlay && this.overlay.destroy();
    }

    // Destroy and rebuild the overlay
    refreshOverlay = (): void => {
        // console.log("Refreshing Overlay!");
        this.clearOverlay();
        this.overlay.create(this.$timeout, this.$sce, this.$rootScope, this.$compile);
    }

    // Manually trigger a tour, bypassing the NudgeTriggers
    triggerTour = (name: string) => {
        let tour = this.tours.find((t) => t.name == name);

        if (!!tour) {
            this.startTour(tour, 0);
        }
    }

    addElementEvents = (): void => {
        if (!this.currentStep || !this.currentStep.events) return;
        for (let eventDef of this.currentStep.events) {
            let $triggerer = angular.element('<div class="nudge-event-triggerer"></div>');
            let $eventEl = angular.element(eventDef.selector);

            if (!['fixed', 'absolute', 'relative'].find((str) => str == $eventEl.css('position'))) {
                $eventEl.css('position', 'relative');
            }

            $eventEl.append($triggerer);

            if (eventDef.action instanceof Function) {
                $triggerer.on(eventDef.event, eventDef.action);
            } else {
                if (eventDef.action.type == "setState") {
                    $triggerer.on(eventDef.event, ($event) => {
                        this.$_userState.setState(eventDef.action.state, eventDef.action.val);
                        $triggerer.remove();
                    })
                } else {
                    // TODO Implement other types of events (like continue tour on same page)
                }
            }
        }
    }

    // Check each tour for triggering based on UI State name
    checkForTourTrigger = (stateName: string): void => {
        this.tours.sort((a, b) => (a.priority || 100) - (b.priority) || 100);
        for (let tour of this.tours) {
            // If user has already completed this tour, do not show again
            if (this.$_userState.getState(tour.userState) == true) {
                // console.log("Tour already completed:", tour.name)
                continue;
            }

            //Check each of the triggers
            for (let trigger of tour.triggers) {
                //if tour can be triggered in this view nd user state, begin
                if (trigger.checkView(stateName) && trigger.checkUserState(this.$_userState, this.$_appState)) {
                    // console.log("Triggered tour:", tour.name);
                    this.startTour(tour, trigger.step);
                    return; //only one tour can be active at time
                }
            }
        }
    }

    // Set the current step of the tour
    setStep = (step: number): void => {
        // console.log(this.activeTour);
        this.currentStep = this.activeTour.steps[(this.currentStepNumber = step)];
    }

    // Begin a tour
    startTour = (tour: NudgeTour, step?: number): void => {
        this.activeTour = tour;
        this.beginStep(step);
    }

    // Initiates the current NudgeStep, locating the elements, creating the overlay, and providing the messaging
    beginStep = (step?: number): void => {
        //safety to remove previous overlay
        let cleared = this.clearOverlay();
        // console.log('Overlay cleared', cleared);


        (step >= 0) && this.setStep(step);
        // console.log("Beginning step", this.currentStepNumber, this.currentStep);

        //create new NudgeOverlay to handle to overlay creation
        this.overlay = new NudgeOverlay(this, this.activeTour, this.currentStepNumber);
        this.addElementEvents();
        this.overlay.create(this.$timeout, this.$sce, this.$rootScope, this.$compile);
        this.setWatchers();
    }

    // End the current step and perform any onSuccess actions
    endStep = (): void => {
        this.clearWatchers();
        // console.log("Ending current step:", this.currentStep);
        if (!!this.currentStep && !!this.currentStep.onSuccess) {
            let def = this.currentStep.onSuccess;
            if (def.action === 'setState') {
                this.$_userState.setState(def.key, def.val);
            }
        }
    }


    clearWatchers = (): void => {
        // console.log("Clearing", (this.sizeWatchers || []).length, "watchers");
        for (let sizeWatcher of this.sizeWatchers || []) {
            sizeWatcher();
        }

        this.sizeWatchers = null;
    }

    setWatchers = (): void => {
        if (!!this.sizeWatchers && !!this.sizeWatchers.length) {
            this.clearWatchers();
        }

        let step = this.currentStep;

        this.sizeWatchers = [
            this.$rootScope.$watch(
                () => {
                    return {
                        'height': $(document).innerHeight()
                    };
                },
                (newVal: number, oldVal: number) => {
                    newVal != oldVal && this.refreshOverlay();
                    // console.log("Document resized");
                },
                true
            )
        ];

        if (!!step.watchers && !!step.watchers.size) {
            for (let selector of step.watchers.size) {
                // console.log("Creating size watcher on", selector);
                this.sizeWatchers.push(this.$rootScope.$watch(
                    () => {
                        return {
                            'height': angular.element(selector).outerHeight()
                        };
                    },
                    (newVal: number, oldVal: number) => {
                        newVal != oldVal && this.refreshOverlay();
                        // console.log(selector, "resized");
                    },
                    true));
            }
        }
    }

    // Continue that activeTour at the specified step if it exists
    continueAtStep = (step: number): void => {
        // TODO Implement manually continuing a tour at any step
    }




    // Continue the active tour or check if a new tour should start.
    postNavigate = (view: string): void => {
        // Either end current step and start next step, or simply end tour
        if (!!this.activeTour && !!this.currentStep) {
            this.beginStep();
        } else {
            this.checkForTourTrigger(view);
        }

    }

    // Test the passed UI State name to see if the activeTour should continue or end.
    preNavigate = (view: string): void => {

        this.clearWatchers();
        let cleared = this.clearOverlay();
        // Either end current step and start next step, or simply end tour
        if (!!this.activeTour && !!this.currentStep) {
            for (let cont of this.currentStep.continues || []) {
                // if Tour set to continue on navigation
                // console.log(cont, view)
                if (cont.action === 'nav' && cont.val === view) {
                    // console.log('Ending step', this.currentStepNumber, 'and continuing next step', cont.step, 'due to navigation');
                    this.endStep();
                    this.setStep(cont.step);
                    return;
                }
            }

            this.endTour();
        }

    }

    //Steps taken once the tour is ended
    endTour = (dontDisable?: boolean): void => {
        this.endStep(); //end the last step

        //If dontDisable is true, the userState describing whether user has completed tour NOT set to true
        !dontDisable && !!this.activeTour && !!this.activeTour.userState && this.$_userState.setState(this.activeTour.userState, true);
        // console.log("Ending tour, userState for tour:", this.activeTour.userState, this.$_userState.getState(this.activeTour.userState));
        this.clearOverlay(); // Destroy the current overlay if there is one
        this.currentStep = null; // Reset the tour specific attributes
        this.currentStepNumber = null;
        this.overlay = null;
        this.activeTour = null;
    }

    // Return true if there is an active tour and no name specified, or if the name is specified that the active tour name matches
    tourIsActive = (name?: string): boolean => !!this.activeTour && (!name || this.activeTour.name === name);

    getToursForView = (viewName: string): NudgeTour[] => {
        let viewTours: NudgeTour[] = [];
        console.log("Checking all tours to see if the can occur on the page", viewName);
        for (let tour of this.tours) {
            console.log("- Tour '" + tour.name + "' can be on the following views:", tour.helpViews || []);
            let helpView = (tour.helpViews || []).find((obj) => obj.view == viewName);

            if (!!helpView) {
                let avail: boolean = true;
                for (let helpState of helpView.appStates) {
                    let fn = this.$_appState.getState(helpState.state);
                    if (!fn(helpState.val)) {
                        console.log("-- AppState '" + helpState.state + "':", fn(helpState.val), "- Not Available");
                        avail = false;
                    }
                }

                !!avail && viewTours.push(tour);
            }
        }

        return viewTours;
    }

}

angular.module('eCareVault.common.services')
    .service('NudgeService', NudgeService)
